import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, ArrowLeft, Sparkles, Lock, LogIn } from "lucide-react";
import Nav from "../components/landing/Nav";
import Footer from "../components/landing/Footer";
import ContactDialog from "../components/landing/ContactDialog";
import ShareBar from "../components/landing/ShareBar";
import ClientOps from "../components/landing/ClientOps";

/**
 * Standalone /clientops page.
 * Purpose: a clean, memorable URL to paste in Facebook / LinkedIn / DMs.
 * Structure mirrors the article pages so the site's rhythm stays consistent:
 * short focused hero → the full ClientOps deep-dive → share bar → CTA back
 * to nztaxation.nz plans.
 */
export default function ClientOpsPage() {
  const [contactOpen, setContactOpen] = useState(false);

  useEffect(() => {
    window.scrollTo({ top: 0, left: 0, behavior: "instant" });
  }, []);

  const url =
    typeof window !== "undefined"
      ? `${window.location.origin}/clientops`
      : "https://nztaxation.nz/clientops";

  const shareTitle = "ClientOps — a financial operating system for NZ small business";
  const shareDescription =
    "Most accountants file your tax once a year. ClientOps reviews your cash flow, business health score, and missing documents every day — built in-house by NZ Taxation Limited and available only to businesses we work with.";

  return (
    <main
      className="min-h-screen bg-[#FAFAFA] text-[#0F172A]"
      data-testid="clientops-page"
    >
      <Nav
        onCtaClick={() => (window.location.href = "/#pricing")}
        onContactClick={() => setContactOpen(true)}
      />

      {/* Compact hero — mirrors the article page pattern for consistency */}
      <section className="relative overflow-hidden border-b border-slate-200">
        <div className="absolute inset-0 bg-grid opacity-60 pointer-events-none" />
        <div className="relative max-w-4xl mx-auto px-6 md:px-10 pt-12 md:pt-20 pb-12 md:pb-16">
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-bold text-slate-500 hover:text-[#C9A349] transition-colors"
            data-testid="clientops-page-back"
          >
            <ArrowLeft className="w-3 h-3" /> nztaxation.nz
          </Link>

          <motion.div
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
            className="mt-8 inline-flex items-center gap-2 border border-slate-300 px-3 py-1.5 text-[11px] uppercase tracking-[0.22em] font-bold text-slate-700 bg-white/70 backdrop-blur"
          >
            <Sparkles className="w-3 h-3 text-[#C9A349]" />
            NZ Taxation Limited · Exclusive to our clients
          </motion.div>

          <motion.h1
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="mt-6 font-heading font-black text-4xl sm:text-5xl md:text-6xl tracking-tight leading-[1.02]"
            data-testid="clientops-page-headline"
          >
            <span className="inline-flex items-center gap-3">
              <Lock className="w-8 h-8 md:w-10 md:h-10 text-[#C9A349]" />
              ClientOps.
            </span>
            <br />
            <span className="text-[#C9A349]">
              A financial operating system, not a compliance tool.
            </span>
          </motion.h1>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.12 }}
            className="mt-6 text-lg text-slate-600 max-w-2xl leading-relaxed"
          >
            {shareDescription}
          </motion.p>

          <div className="mt-8 flex flex-wrap items-center gap-3">
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 bg-[#0A192F] text-white px-6 py-4 font-heading font-bold uppercase tracking-[0.12em] text-sm hover:bg-[#C9A349] hover:text-[#0A192F] transition-colors"
              data-testid="clientops-page-cta-pricing"
            >
              See monthly plans <ArrowRight className="w-4 h-4" />
            </Link>
            <a
              href="https://www.clientops.nz/?utm_source=nztaxation-clientops-page"
              target="_blank"
              rel="noreferrer"
              title="ClientOps — Client data flow for accounting firms"
              className="inline-flex items-center gap-2 border border-[#0A192F] px-6 py-4 font-heading font-bold uppercase tracking-[0.12em] text-sm text-[#0A192F] hover:bg-[#0A192F] hover:text-white transition-colors"
              data-testid="clientops-page-cta-signin"
            >
              <LogIn className="w-4 h-4" /> Existing client · sign in
            </a>
          </div>

          <div className="mt-8">
            <ShareBar url={url} title={shareTitle} description={shareDescription} />
          </div>
        </div>
      </section>

      {/* Reuse the same ClientOps deep-dive that lives on the landing page */}
      <ClientOps />

      {/* Closing share strip + CTA */}
      <section className="bg-[#0A192F] text-white">
        <div className="max-w-5xl mx-auto px-6 md:px-10 py-16 md:py-24">
          <div className="text-[11px] uppercase tracking-[0.22em] font-bold text-[#C9A349]">
            Like what you see? Share it forward.
          </div>
          <h2 className="mt-4 font-heading font-black text-3xl md:text-5xl tracking-tight leading-[1.05]">
            Tell another Kiwi business owner
            <br />
            <span className="text-[#C9A349]">what modern accounting looks like.</span>
          </h2>
          <p className="mt-5 text-slate-300 max-w-2xl text-lg">
            Copy this page's link, paste it in Messenger, Facebook, or LinkedIn — anyone who
            reads it lands on nztaxation.nz and can pick a plan directly.
          </p>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link
              to="/#pricing"
              className="inline-flex items-center gap-2 bg-[#C9A349] text-[#0A192F] px-6 py-4 font-heading font-bold uppercase tracking-[0.12em] text-sm hover:bg-white transition-colors"
              data-testid="clientops-page-cta-plans-bottom"
            >
              See monthly plans <ArrowRight className="w-4 h-4" />
            </Link>
            <button
              onClick={() => setContactOpen(true)}
              className="inline-flex items-center gap-2 border border-white/30 px-6 py-4 font-medium hover:bg-white/10 transition-colors"
              data-testid="clientops-page-cta-email"
            >
              Email the team
            </button>
          </div>

          <div className="mt-12">
            <ShareBar url={url} title={shareTitle} description={shareDescription} />
          </div>
        </div>
      </section>

      <Footer />
      <ContactDialog open={contactOpen} onOpenChange={setContactOpen} />
    </main>
  );
}
